import { deepOrange, green, grey, yellow } from "@mui/material/colors";
// Shared with the export's "Vendor Stats" sheet (export-data lambda) so the
// sheet's values format exactly like the tiles' — re-exported here to keep
// the dashboard's historical import sites stable.
import { complianceHeadline, formatPercent } from "encamp-shared/src/waste/vendorStatsFormat";

import {
  CORRECTION_TRACKING_START_YEAR,
  correctionDataEmptyMessage,
  formatDays,
  lastVisibleMonth,
  MONTH_LABELS,
} from "./correctionTracking";

export { complianceHeadline, formatPercent };

// Aligns with the waste-overview palette (apps/oak/.../Waste/Overview/Tiles/
// Charts/constants.ts): named greens for "good", yellow/orange for severity,
// grey for neutral/pending. Avoids the harsher theme.palette.error.main red
// the rest of the app uses for hard errors.
export const COMPLIANCE_COLORS = {
  onTime: green[400],
  late: yellow[700],
  overdue: deepOrange[300],
  pending: grey[300],
} as const;

export interface CorrectionStats {
  onTime: number;
  late: number;
  overdue: number;
  inWindow: number;
}

export interface ComplianceSegment {
  key: "onTime" | "late" | "overdue" | "inWindow";
  /** The correctionBucket value used by the grid omnisearch filter. */
  bucket: "ON_TIME" | "LATE" | "OVERDUE" | "PENDING";
  label: string;
  value: number;
  color: string;
}

// The donut's composition segments, in display order, with the empty ones
// dropped so a zero-value bucket never renders an invisible arc or a dead
// legend entry. Each segment carries the omnisearch `bucket` it filters to.
export function buildComplianceSegments(stats: CorrectionStats): ComplianceSegment[] {
  const allSegments: ComplianceSegment[] = [
    {
      key: "onTime",
      bucket: "ON_TIME",
      label: "Resolved on time",
      value: stats.onTime,
      color: COMPLIANCE_COLORS.onTime,
    },
    {
      key: "late",
      bucket: "LATE",
      label: "Late",
      value: stats.late,
      color: COMPLIANCE_COLORS.late,
    },
    {
      key: "overdue",
      bucket: "OVERDUE",
      label: "Overdue",
      value: stats.overdue,
      color: COMPLIANCE_COLORS.overdue,
    },
    {
      key: "inWindow",
      bucket: "PENDING",
      label: "Pending",
      value: stats.inWindow,
      color: COMPLIANCE_COLORS.pending,
    },
  ];
  return allSegments.filter((seg) => seg.value > 0);
}

// The donut is empty only when nothing has concluded AND nothing is pending.
// Pending-only is NOT empty — the pending segment still renders and stays
// click-to-filterable, so the empty-state copy must not hide it.
export function isCorrectionStatsEmpty(stats: CorrectionStats): boolean {
  return stats.onTime + stats.late + stats.overdue === 0 && stats.inWindow === 0;
}

// Copy for the empty donut. A vendor narrowing inside the tracked era gets a
// vendor-specific line; otherwise (and always pre-2026, when the whole year is
// empty by definition because tracking was external) the epoch message wins.
export function correctionComplianceEmptyMessage(
  shippedYear: number,
  selectedVendor: string | null
): string {
  if (selectedVendor && shippedYear >= CORRECTION_TRACKING_START_YEAR) {
    return `No correction requests for ${selectedVendor} in ${shippedYear}.`;
  }
  return correctionDataEmptyMessage(shippedYear);
}

export interface MonthlyComplianceInput {
  month: number;
  /** Concluded requests (onTime + late + overdue) — the % Compliant denominator. */
  total: number;
  corrected30: number;
  onTime: number;
  late: number;
  overdue: number;
  pending: number;
  avgDaysToCorrect?: number | null;
}

// A `type` (not `interface`) so it carries an implicit index signature and
// stays assignable to MUI x-charts' `DatasetType` when handed to <BarChart>.
export type MonthlyComplianceDatum = {
  month: number;
  monthLabel: string;
  onTime: number;
  late: number;
  overdue: number;
  pending: number;
  other: number;
  /** Every bucket, pending included — the gray background bar's height. */
  allTotal: number;
  tooltipTitle: string;
};

// Sum of every bucket for a month row — the bar's full height. Distinct from
// the row's `total`, which is the concluded subset behind % Compliant.
function allBuckets(row: MonthlyComplianceInput | undefined): number {
  if (!row) return 0;
  return row.onTime + row.late + row.overdue + row.pending;
}

// Builds the monthly-trend dataset for the compliance bar chart. Two stacks
// share each x position: the static gray all-bucket month total (`allTotal`)
// behind, and the focused colored portion — the donut's four buckets — in
// front. When a vendor is selected the focused buckets show that vendor's
// numbers and `other` carries the remaining (all-vendor minus this-vendor)
// total so the gray "other vendors" remainder shows through; with no vendor
// selected `other` is zero everywhere. The series structure stays constant
// either way so the chart only re-values on selection, never re-lays-out.
export function buildMonthlyComplianceDataset({
  summaryRows,
  vendorRows,
  selectedVendor,
  shippedYear,
}: {
  summaryRows: MonthlyComplianceInput[];
  vendorRows: MonthlyComplianceInput[];
  selectedVendor: string | null;
  shippedYear: number;
}): MonthlyComplianceDatum[] {
  const byMonth = new Map<number, MonthlyComplianceInput>();
  summaryRows.forEach((row) => byMonth.set(row.month, row));
  const vendorByMonth = new Map<number, MonthlyComplianceInput>();
  vendorRows.forEach((row) => vendorByMonth.set(row.month, row));

  const visibleMonths = lastVisibleMonth(shippedYear, [...byMonth.keys(), ...vendorByMonth.keys()]);

  return MONTH_LABELS.slice(0, visibleMonths).map((label, i) => {
    const month = i + 1;
    const row = byMonth.get(month);
    const vendorRow = vendorByMonth.get(month);

    const fg = selectedVendor ? vendorRow : row;
    const allTotal = allBuckets(row);
    const other = selectedVendor ? Math.max(allTotal - allBuckets(vendorRow), 0) : 0;

    // % compliant computes over the concluded total (pending excluded),
    // matching the donut's headline.
    const displayCorrected = fg?.corrected30 ?? 0;
    const displayTotal = fg?.total ?? 0;
    const displayAvg = fg?.avgDaysToCorrect;

    return {
      month,
      monthLabel: label,
      onTime: fg?.onTime ?? 0,
      late: fg?.late ?? 0,
      overdue: fg?.overdue ?? 0,
      pending: fg?.pending ?? 0,
      other,
      allTotal,
      tooltipTitle:
        displayTotal > 0
          ? `${label} · ${formatPercent(displayCorrected, displayTotal)} compliant · avg ${formatDays(displayAvg)}`
          : label,
    };
  });
}
